import React from "react";
import { Link } from "react-router-dom";
import { formatAmount } from "@/lib/currency";
import { useApp } from "@/context/AppContext";
import { prefetchPlan } from "@/lib/planCache";
import { Bed, Bath, Square, Layers, Car, Heart, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { enabledDownloadTiers, fromPrice } from "@/lib/packages";

const PlanCard = ({ plan, floorPlanFirst = false, channel = "premium" }) => {
  const { currency, toggleWishlist, inWishlist, toggleCompare, inCompare, compareList, COMPARE_MAX, openCompare } = useApp();
  // Iter60A — Floor Plans marketplace prioritises the colored floor plan
  // (or B&W fallback) as the card hero. Premium Browse Plans keeps its
  // rendering-first cover. Both surfaces share this single component.
  const pickFloorPlanCover = () => {
    const drawings = plan.technical_drawings || [];
    const colored = drawings.find((d) => d.type === "floor_plan_colored");
    const bw = drawings.find((d) => d.type === "floor_plan_bw" || d.type === "floor_plan");
    return colored?.url || bw?.url || plan.floor_plan_images?.[0] || plan.images?.[0];
  };
  const cover = (floorPlanFirst && pickFloorPlanCover())
    || plan.images?.[0]
    || plan.floor_plan_images?.[0]
    || "https://images.pexels.com/photos/7031581/pexels-photo-7031581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
  const wishlisted = inWishlist(plan.id);
  const comparing = inCompare(plan.id);

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(plan);
    toast.success(wishlisted ? "Removed from wishlist" : "Added to wishlist", { description: plan.title });
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!comparing && compareList.length >= COMPARE_MAX) {
      toast.error(`You can compare up to ${COMPARE_MAX} plans`, {
        description: "Remove one to add another",
        action: { label: "Open compare", onClick: openCompare },
      });
      return;
    }
    toggleCompare(plan);
    toast.success(comparing ? "Removed from compare" : "Added to compare", {
      description: plan.title,
      action: !comparing ? { label: "View compare", onClick: openCompare } : undefined,
    });
  };

  return (
    <Link
      to={`/plans/${plan.id}${channel === "floor_plan" ? "?channel=floor_plan" : ""}`}
      data-testid={`plan-card-${plan.id}`}
      onMouseEnter={() => prefetchPlan(plan.id, cover)}
      onFocus={() => prefetchPlan(plan.id, cover)}
      onTouchStart={() => prefetchPlan(plan.id, cover)}
      className="group block plan-card-hover border border-border bg-background"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={cover}
          alt={`${plan.title} — ${plan.style} house plan, ${plan.bedrooms} bed, ${plan.sqft} sqft`}
          className="plan-card-img w-full h-full object-cover"
          loading="lazy"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          data-protected="true"
        />
        {plan.style && (
          <div className="absolute top-3 left-3 bg-background/95 backdrop-blur px-3 py-1 overline text-accent">
            {plan.style}
          </div>
        )}
        <button
          onClick={handleWishlist}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          data-testid={`wishlist-${plan.id}`}
          className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center border transition-fast ${wishlisted ? "bg-accent border-accent text-accent-foreground" : "bg-background/95 backdrop-blur border-transparent hover:border-accent hover:text-accent"}`}
        >
          <Heart size={16} fill={wishlisted ? "currentColor" : "none"} />
        </button>
        <button
          onClick={handleCompare}
          aria-label={comparing ? "Remove from compare" : "Add to compare"}
          data-testid={`compare-toggle-${plan.id}`}
          className={`absolute top-14 right-3 w-9 h-9 flex items-center justify-center border transition-fast ${comparing ? "bg-[#2C4C3B] border-[#2C4C3B] text-white" : "bg-background/95 backdrop-blur border-transparent hover:border-[#2C4C3B] hover:text-[#2C4C3B]"}`}
        >
          <GitCompare size={16} />
        </button>
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl leading-tight text-foreground group-hover:text-accent transition-fast">
          {plan.title}
        </h3>
        {plan.project_id && (
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`plan-project-id-${plan.id}`}>
            {plan.project_id}
          </div>
        )}
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
          {plan.short_description}
        </p>

        <div className="mt-5 grid grid-cols-5 gap-2 text-[11px] text-muted-foreground">
          <span className="flex flex-col items-center gap-1"><Bed size={14} className="text-accent" />{plan.bedrooms ?? "—"}<span className="text-[9px] uppercase tracking-wider">bed</span></span>
          <span className="flex flex-col items-center gap-1"><Bath size={14} className="text-accent" />{plan.bathrooms ?? "—"}<span className="text-[9px] uppercase tracking-wider">bath</span></span>
          <span className="flex flex-col items-center gap-1"><Layers size={14} className="text-accent" />{plan.stories ?? "—"}<span className="text-[9px] uppercase tracking-wider">story</span></span>
          <span className="flex flex-col items-center gap-1"><Car size={14} className="text-accent" />{plan.garage ?? "—"}<span className="text-[9px] uppercase tracking-wider">gar</span></span>
          <span className="flex flex-col items-center gap-1"><Square size={14} className="text-accent" />{typeof plan.sqft === "number" ? plan.sqft.toLocaleString() : "—"}<span className="text-[9px] uppercase tracking-wider">sqft</span></span>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
          {(() => {
            // Iter60A — channel-aware "From" price. Uses only tiers valid
            // for the current marketplace (Premium: pdf/cad/revit ; Floor
            // Plans: fp_pdf/fp_cad/fp_revit — fp_customize excluded).
            // Legacy plan.price_usd is NOT used as a fallback.
            const tiers = enabledDownloadTiers(plan, channel);
            if (tiers.length > 0) {
              const price = fromPrice(plan, currency, channel);
              return (
                <span data-testid={`plan-price-${plan.id}`} className="font-display text-2xl text-primary flex items-baseline gap-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-sans">from</span>
                  {formatAmount(price, currency)}
                </span>
              );
            }
            return (
              <span data-testid={`plan-price-${plan.id}`} className="text-xs uppercase tracking-widest text-muted-foreground">
                Price on request
              </span>
            );
          })()}
          <span className="overline text-accent group-hover:underline">View →</span>
        </div>
      </div>
    </Link>
  );
};

export default PlanCard;
